import React, { useEffect, useState } from "react";
import { Alert, Col, ConfigProvider, Empty, Row, Spin } from "antd";
import { ClientService } from "../../services/clients/ClientService";
import { PublicClientSummary } from "../../model/clients/PublicClientSummary";
import { buildTenantUrl } from "../../utils/extractSubdomainSlug";
import { fortiusProfileTheme } from "../membership/profile/v2/fortiusTheme";
import "./fortius-clubs.css";

// A club is only selectable if its slug resolves to a valid tenant URL. The slug
// comes from the public API payload, so we treat it as untrusted and drop any
// row that fails the canonical slug check (buildTenantUrl returns null).
interface SelectableClub {
    club: PublicClientSummary;
    url: string;
}

// Only http(s) logos are rendered as <img src>. Branding is tenant-controlled in
// a multi-tenant system, so reject anything that isn't a plain web URL rather
// than feeding arbitrary strings (e.g. javascript:, data:) into the DOM.
const isSafeImageSrc = (value: string | undefined): value is string => {
    if (!value) return false;
    try {
        const { protocol } = new URL(value);
        return protocol === "https:" || protocol === "http:";
    } catch {
        return false;
    }
};

// Monogram for clubs without a logo — keeps the grid reading as a consistent set
// of plates instead of half-empty cards. Up to two initials from the club name.
const initialsOf = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase() ?? "")
        .join("") || "•";

// Renders the club logo when present and loadable, otherwise an initials
// monogram. Tenant-supplied logo URLs can be stale or broken, so we fall back on
// the image's error event rather than leaving a broken-image icon in the grid.
const ClubLogo: React.FC<{ club: PublicClientSummary }> = ({ club }) => {
    const [failed, setFailed] = useState(false);
    const showImage = !failed && isSafeImageSrc(club.branding.logoUrl);

    return (
        <div className="ft-club-logo">
            {showImage ? (
                <img
                    src={club.branding.logoUrl}
                    alt={`${club.clientName} logo`}
                    onError={() => setFailed(true)}
                />
            ) : (
                <div className="ft-club-monogram" aria-hidden="true">
                    {initialsOf(club.clientName)}
                </div>
            )}
        </div>
    );
};

/**
 * Public, unauthenticated club selector shown on the apex (fortius-app.com).
 * Lists the listable clubs from GET /clients/public and, on selection, sends the
 * browser to that tenant's subdomain where the existing per-subdomain Auth0 login
 * runs. Renders without a resolved tenant — it is the entry point *before* one is
 * chosen — so it must not depend on ClientContext.
 *
 * Styled to match the Fortius V2 design language (Bookings V2 / Member Profile
 * V2): teal accent, ink/muted neutrals, tight card grid, scoped under `.ft-clubs`.
 */
const ClubsPage: React.FC = () => {
    const [clubs, setClubs] = useState<SelectableClub[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const summaries = await ClientService.listPublicClients();
                if (cancelled) return;
                const selectable = summaries
                    .map((club) => ({ club, url: buildTenantUrl(club.subdomainSlug) }))
                    .filter((entry): entry is SelectableClub => entry.url !== null);
                setClubs(selectable);
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <ConfigProvider theme={fortiusProfileTheme}>
            <div className="ft-clubs" data-testid="clubs-page">
                <div className="ft-clubs-shell">
                    <div className="ft-clubs-hero">
                        <img
                            className="ft-clubs-mark"
                            src="/logo-square-dark.svg"
                            alt="Fortius"
                            width={48}
                            height={48}
                        />
                        <div className="ft-clubs-eyebrow">Fortius</div>
                        <h1 className="ft-clubs-title">Choose your club</h1>
                        <p className="ft-clubs-sub">
                            Select your club to continue to log in.
                        </p>
                    </div>

                    {loading && (
                        <div className="ft-clubs-panel" data-testid="clubs-loading">
                            <Spin size="large" />
                            <div className="ft-clubs-panel-note">Loading clubs…</div>
                        </div>
                    )}

                    {!loading && error && (
                        <Alert
                            data-testid="clubs-error"
                            type="error"
                            showIcon
                            message="We couldn't load the list of clubs"
                            description="Please try again in a moment."
                        />
                    )}

                    {!loading && !error && clubs.length === 0 && (
                        <div className="ft-clubs-panel">
                            <Empty data-testid="clubs-empty" description="No clubs are available yet." />
                        </div>
                    )}

                    {!loading && !error && clubs.length > 0 && (
                        <Row gutter={[16, 16]} data-testid="clubs-list">
                            {clubs.map(({ club, url }) => (
                                <Col key={club.subdomainSlug} xs={24} sm={12} md={8}>
                                    <a href={url} data-testid={`club-card-${club.subdomainSlug}`}>
                                        <div className="ft-club-card">
                                            <ClubLogo club={club} />
                                            <div className="ft-club-name">{club.clientName}</div>
                                            <span className="ft-club-cta">
                                                Continue
                                                <span className="arw" aria-hidden="true">
                                                    →
                                                </span>
                                            </span>
                                        </div>
                                    </a>
                                </Col>
                            ))}
                        </Row>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default ClubsPage;
